import React, { useEffect, useMemo, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import { PageHeader, StatusPill } from "@/components/admin/AdminUI";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Link, useNavigate } from "react-router-dom";
import { apiListHandovers, apiListProjects, formatApiError } from "@/lib/api";
import { formatShortDate } from "@/lib/date";
import { toast } from "sonner";
import CreateHandoverDialog from "@/components/handover/CreateHandoverDialog";
import { KeywordSearch, IncidentTrail } from "@/components/handover/HandoverSearchViews";
import { Plus, ArrowRight, ClipboardList, Search, Hash } from "lucide-react";

const STATUS_OPTIONS = [
    { value: "all", label: "All" },
    { value: "Draft", label: "Draft" },
    { value: "Submitted", label: "Submitted" },
    { value: "Acknowledged", label: "Acknowledged" },
];

export default function Handovers() {
    const { user } = useAuth();
    const isClient = user?.role === "Client";
    const canCreate = !isClient;
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [projectId, setProjectId] = useState("all");
    const [status, setStatus] = useState("all");
    const [mineOnly, setMineOnly] = useState(false);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [tab, setTab] = useState("list"); // list | search | trail

    const authorizedKey = (user?.authorized_projects || []).join(",");

    useEffect(() => {
        (async () => {
            try {
                let list = await apiListProjects();
                if (isClient) {
                    const allow = new Set(user?.authorized_projects || []);
                    list = list.filter((p) => allow.has(p.project_id));
                }
                setProjects(list);
            } catch (err) {
                toast.error(formatApiError(err));
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isClient, authorizedKey]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await apiListHandovers({
                project_id: projectId === "all" ? undefined : projectId,
                status: status === "all" ? undefined : status,
                mine: mineOnly,
            });
            setRows(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [projectId, status, mineOnly]);

    useEffect(() => {
        load();
    }, [load]);

    const projectDir = useMemo(() => {
        const m = {};
        projects.forEach((p) => (m[p.project_id] = p));
        return m;
    }, [projects]);

    return (
        <Layout active="handovers">
            <PageHeader
                title="Handovers"
                subtitle={
                    isClient
                        ? "Submitted and acknowledged handovers for your project."
                        : "Draft, submit, and acknowledge shift handovers."
                }
                actions={
                    canCreate && tab === "list" ? (
                        <Button
                            onClick={() => setCreateOpen(true)}
                            data-testid="new-handover-btn"
                            className="h-9 bg-[#0072BC] hover:bg-[#005a94] text-white"
                        >
                            <Plus className="h-4 w-4 mr-1.5" />
                            New handover
                        </Button>
                    ) : null
                }
            />

            {/* Tabs */}
            <div
                className="inline-flex items-center rounded-md border border-ntt-border bg-white overflow-hidden mb-4"
                data-testid="handovers-tabs"
            >
                {[
                    { key: "list", label: "History", icon: ClipboardList },
                    { key: "search", label: "Search", icon: Search },
                    { key: "trail", label: "Incident trail", icon: Hash },
                ].map((t) => {
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.key}
                            type="button"
                            data-testid={`tab-${t.key}`}
                            onClick={() => setTab(t.key)}
                            className={`h-9 px-3 text-[12px] font-medium inline-flex items-center gap-1.5 transition-colors ${
                                tab === t.key
                                    ? "bg-[#0072BC] text-white"
                                    : "text-[#2B2F3A] hover:bg-[#F4F6F9]"
                            }`}
                        >
                            <Icon className="h-3.5 w-3.5" />
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {tab === "list" && (
                <ListFilters
                    projects={projects}
                    projectId={projectId}
                    setProjectId={setProjectId}
                    status={status}
                    setStatus={setStatus}
                    mineOnly={mineOnly}
                    setMineOnly={setMineOnly}
                    isClient={isClient}
                />
            )}

            {tab === "search" && <KeywordSearch projectDir={projectDir} />}
            {tab === "trail" && <IncidentTrail projectDir={projectDir} />}

            {tab === "list" && (
                <div className="bg-white border border-ntt-border rounded-xl overflow-hidden mt-4" data-testid="handovers-table">
                <div className="grid grid-cols-[120px_minmax(160px,1.4fr)_120px_minmax(220px,2fr)_120px_120px] gap-4 px-5 py-3 border-b border-ntt-border bg-[#F4F6F9] text-[11px] font-medium uppercase tracking-wider text-ntt-muted">
                    <div>Date</div>
                    <div>Project</div>
                    <div>Shift</div>
                    <div>Outgoing → Incoming</div>
                    <div>Status</div>
                    <div className="text-right">Open</div>
                </div>
                {loading ? (
                    <div className="p-8 text-center text-sm text-ntt-muted">
                        Loading handovers…
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-10 text-center">
                        <ClipboardList className="h-6 w-6 mx-auto text-ntt-muted mb-2" />
                        <div className="text-sm text-ntt-muted mb-3">
                            No handovers to show.
                        </div>
                        {canCreate && (
                            <Button
                                onClick={() => setCreateOpen(true)}
                                className="bg-[#0072BC] hover:bg-[#005a94] text-white"
                            >
                                Start a handover
                            </Button>
                        )}
                    </div>
                ) : (
                    rows.map((h) => {
                        const project = projectDir[h.project_id];
                        return (
                            <div
                                key={h.handover_id}
                                data-testid={`handover-row-${h.handover_id}`}
                                className="grid grid-cols-[120px_minmax(160px,1.4fr)_120px_minmax(220px,2fr)_120px_120px] gap-4 px-5 py-3 border-b border-ntt-border last:border-0 items-center hover:bg-[#FBFCFE]"
                            >
                                <div className="text-[13px] font-mono text-[#2B2F3A]">
                                    {formatShortDate(h.handover_date)}
                                </div>
                                <div className="text-[13px] font-medium text-[#070F26] truncate">
                                    {project?.name || h.project_code || h.project_id}
                                </div>
                                <div className="text-[13px] text-[#2B2F3A]">
                                    {h.shift_name || h.shift_id}
                                </div>
                                <div className="text-[12px] text-[#2B2F3A] truncate">
                                    <span className="font-medium">
                                        {h.outgoing_user_name || "—"}
                                    </span>
                                    <span className="text-ntt-muted mx-1">→</span>
                                    <span>
                                        {h.incoming_user_name || (
                                            <em className="text-ntt-muted">
                                                open to claim
                                            </em>
                                        )}
                                    </span>
                                </div>
                                <div>
                                    <StatusPill
                                        label={h.status}
                                        tone={
                                            h.status === "Acknowledged"
                                                ? "green"
                                                : h.status === "Submitted"
                                                  ? "blue"
                                                  : "gray"
                                        }
                                    />
                                </div>
                                <div className="flex items-center justify-end">
                                    <Link
                                        to={`/handovers/${h.handover_id}`}
                                        data-testid={`open-handover-${h.handover_id}`}
                                        className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-ntt-border text-[12px] font-medium text-[#0072BC] hover:bg-[#F4F6F9]"
                                    >
                                        Open
                                        <ArrowRight className="h-3.5 w-3.5" />
                                    </Link>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
            )}

            <CreateHandoverDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                onCreated={(ho) => navigate(`/handovers/${ho.handover_id}`)}
            />
        </Layout>
    );
}

function ListFilters({ projects, projectId, setProjectId, status, setStatus, mineOnly, setMineOnly, isClient }) {
    return (
        <div className="flex flex-wrap items-center gap-2">
            <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger className="h-9 w-[200px]" data-testid="handovers-project-trigger">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">All projects</SelectItem>
                    {projects.map((p) => (
                        <SelectItem key={p.project_id} value={p.project_id} data-testid={`handovers-project-option-${p.project_id}`}>
                            {p.name} · {p.code}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-9 w-[150px]" data-testid="handovers-status-trigger">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
            {!isClient && (
                <label className="hidden md:flex items-center gap-2 border border-ntt-border rounded-md h-9 px-3 bg-white text-[12px]">
                    <Switch checked={mineOnly} onCheckedChange={setMineOnly} data-testid="handovers-mine-switch" />
                    Only mine
                </label>
            )}
        </div>
    );
}
